import {useCallback, useRef, useState} from 'react';
import {getAiContentVerificationFailure, verifyAiContent} from '@Libraries/ai-content-verification.library';
import type {AiContentVerification, AiContentVerificationFailure, AiContentVerificationState} from '@Libraries/ai-content-verification.types';
import {INITIAL_AI_CONTENT_VERIFICATION_STATE} from '@Libraries/ai-content-verification.types';

/**
 * Only the latest file's response is applied, so a slow earlier check can never overwrite the result of a newer one.
 */
export function useAiContentVerification(): AiContentVerification {
  const [verificationState, setVerificationState] = useState<AiContentVerificationState>(INITIAL_AI_CONTENT_VERIFICATION_STATE);
  const latestFileRef = useRef<File | null>(null);

  const selectFile = useCallback((file: File | null): void => {
    latestFileRef.current = file;
    setVerificationState({...INITIAL_AI_CONTENT_VERIFICATION_STATE, file});
  }, []);

  const rejectFile = useCallback((file: File, failure: AiContentVerificationFailure): void => {
    latestFileRef.current = file;
    setVerificationState({file, isVerifying: false, result: null, failure});
  }, []);

  const verifyFile = useCallback(async (file: File): Promise<void> => {
    latestFileRef.current = file;
    setVerificationState({file, isVerifying: true, result: null, failure: null});

    try {
      const result = await verifyAiContent(file);
      applyIfLatest(file, {file, isVerifying: false, result, failure: null});
    } catch (error) {
      applyIfLatest(file, {file, isVerifying: false, result: null, failure: getAiContentVerificationFailure(error)});
    }
  }, []);

  const resetVerification = useCallback((): void => {
    selectFile(null);
  }, [selectFile]);

  function applyIfLatest(file: File, nextState: AiContentVerificationState): void {
    if (latestFileRef.current !== file) {
      return;
    }

    setVerificationState(nextState);
  }

  return {verificationState, selectFile, verifyFile, rejectFile, resetVerification};
}
